import { useEffect } from "react";
import { useDebounceEffect, useSetState } from "ahooks";
import { Select, DatePicker, Spin } from "antd";
import { isArray } from "lodash-es";

import OutGoingCall from "./charts/OutGoingCall.tsx";
import IncomingCallTrends from "./charts/IncomingCallTrends.tsx";
import { getMonitor } from "@/services/saleOpServices.ts";
import { getEnums } from "@/services/homeService.ts";
import { getNumberUnit } from "@/pages/roomMeasurementTools/roomMeasurementManage/utils.ts";
import svg1 from "@/assets/measureChart1.svg";
import svg2 from "@/assets/measureChart2.svg";
import svg3 from "@/assets/measureChart3.svg";
import svg4 from "@/assets/measureChart4.svg";
import styles from "@/pages/callTool/outboundCallAnalysis/list/callMonitoring/charts/index.module.less";

// 定义 state 类型
interface CallMonitoringState {
  loading: boolean;
  callChart: any[];
  answerRateChart: any[];
  callDurationChart: any[];
  call24HourChart: any[];
  serviceEnum: { key: string; description: string }[];
  searchValues: {
    service?: string;
    startTime?: string;
    endTime?: string;
  };
  outTotal?: number;
  answerNum?: number;
  notAnswerNum?: number;
  answerRate?: number;
}

export default function CallMonitoring() {
  const [state, setState] = useSetState<CallMonitoringState>({
    loading: false,
    callChart: [],
    answerRateChart: [],
    callDurationChart: [],
    call24HourChart: [],
    serviceEnum: [],
    searchValues: {},
  });

  useEffect(() => {
    getOptions();
  }, []);

  useDebounceEffect(() => {
    getData(state.searchValues);
  }, [state.searchValues], { wait: 500 });

  const getData = (data: Record<string, any>) => {
    setState({ loading: true });
    getMonitor(data)
      .then((res) => {
        const {
          callChart = [],
          answerRateChart = [],
          callDurationChart = [],
          call24HourChart = [],
          outTotal,
          answerNum,
          notAnswerNum,
          answerRate,
        } = res || {};
        setState({
          callChart,
          answerRateChart,
          callDurationChart,
          call24HourChart,
          outTotal,
          answerNum,
          notAnswerNum,
          answerRate,
        });
      })
      .finally(() => {
        setState({ loading: false });
      });
  };

  const getOptions = async () => {
    const serviceEnum = await getEnums("report", "com.shj.saas.report.enums.crm.AudioLogBrandEnum");
    setState({ serviceEnum });
  };

  const onChangeDate = (value: any) => {
    if (isArray(value)) {
      setState({
        searchValues: {
          ...state.searchValues,
          startTime: value[0]?.format("YYYY-MM-DD"),
          endTime: value[1]?.format("YYYY-MM-DD"),
        },
      });
    } else {
      setState({
        searchValues: {
          ...state.searchValues,
          startTime: undefined,
          endTime: undefined,
        },
      });
    }
  };

  const updateSearchValues = (newValues: Record<string, any>) => {
    setState({
      searchValues: {
        ...state.searchValues,
        ...newValues,
      },
    });
  };

  return (
    <Spin spinning={state.loading}>
      <div className={styles.wrapBox}>
        <div className={styles.searchBox}>
          外呼服务商：
          <Select
            style={{ width: 200 }}
            placeholder={"请选择外呼服务商"}
            options={state.serviceEnum}
            fieldNames={{ label: "description", value: "key" }}
            onChange={(value) => updateSearchValues({ service: value })}
            allowClear
          />
          <DatePicker.RangePicker
            style={{ width: 300 }}
            onChange={onChangeDate}
            allowClear
          />
        </div>
        <div className={styles.rowBox}>
          <div className={styles.colBox}>
            <div>
              <p>外呼总数</p>
              <p className={styles.num}>
                {getNumberUnit(state.outTotal || 0, "", 0)?.numberValue ?? 0}
              </p>
            </div>
            <img src={svg1} alt={"外呼总数"} />
          </div>
          <div className={styles.colBox}>
            <div>
              <p>接通数</p>
              <p className={styles.num}>
                {getNumberUnit(state.answerNum || 0, "", 0)?.numberValue ?? 0}
              </p>
            </div>
            <img src={svg2} alt={"接通数"} />
          </div>
          <div className={styles.colBox}>
            <div>
              <p>未接数</p>
              <p className={styles.num}>
                {getNumberUnit(state.notAnswerNum || 0, "", 0)?.numberValue ?? 0}
              </p>
            </div>
            <img src={svg3} alt={"未接数"} />
          </div>
          <div className={styles.colBox}>
            <div>
              <p>接通率</p>
              <p className={styles.num}>{state.answerRate || 0}%</p>
            </div>
            <img src={svg4} alt={"接通率"} />
          </div>
        </div>
      </div>
      <OutGoingCall {...state} />
      <IncomingCallTrends {...state} />
    </Spin>
  );
}
